import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Mic, Square, Pause, Play, Trash2, Upload, Loader2, Sparkles, Save, Check, Volume2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Languages hint for Whisper. 'auto' = let Whisper autodetect.
const LANGUAGES = [
  { code: 'sv', label: 'Svenska' },
  { code: '',   label: 'Auto' },
  { code: 'en', label: 'English' },
  { code: 'da', label: 'Dansk' },
  { code: 'no', label: 'Norsk' },
  { code: 'fi', label: 'Suomi' },
];

function fmtTime(s) {
  if (!Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export default function VoiceRecorderTab() {
  // Recording state
  const [state, setState] = useState('idle'); // idle | recording | paused | stopped
  const [elapsed, setElapsed] = useState(0);
  const [level, setLevel] = useState(0);       // audio level 0-1 for waveform
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const pausedTotalRef = useRef(0);
  const pauseStartRef = useRef(0);

  // Audio analyser for waveform
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);

  // Results
  const [lang, setLang] = useState(() => localStorage.getItem('voice_lang') || 'sv');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // {text, summary, note_id}

  // History
  const [history, setHistory] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => { localStorage.setItem('voice_lang', lang); }, [lang]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/voice-notes/list`, { withCredentials: true });
      setHistory(data || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Cleanup on unmount
  useEffect(() => () => {
    stopAll();
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stopAll = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    audioCtxRef.current = null;
    analyserRef.current = null;
  };

  const startLevelMeter = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      analyserRef.current = analyser;
      const data = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        if (!analyserRef.current) return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        setLevel(Math.min(1, Math.sqrt(sum / data.length) * 2.5));
        levelRafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch { /* ignore */ }
  };

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Mikrofon stöds inte i denna webbläsare 🌸');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      // Prefer webm/opus, fall back to browser default
      let mimeType = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder?.isTypeSupported?.(mimeType)) mimeType = 'audio/webm';
      if (!window.MediaRecorder?.isTypeSupported?.(mimeType)) mimeType = '';
      const rec = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        setAudioBlob(blob);
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
        setState('stopped');
        stopAll();
      };
      rec.start(500);
      startedAtRef.current = Date.now();
      pausedTotalRef.current = 0;
      setElapsed(0);
      setState('recording');
      setResult(null);
      setAudioBlob(null);
      if (audioUrl) { URL.revokeObjectURL(audioUrl); setAudioUrl(null); }
      startLevelMeter(stream);
      timerRef.current = setInterval(() => {
        const active = Date.now() - startedAtRef.current - pausedTotalRef.current;
        setElapsed(active / 1000);
        if (active > 15 * 60 * 1000) { // 15 min cap
          toast('Max 15 min — stoppar inspelningen nu 🌸');
          stop();
        }
      }, 250);
    } catch (e) {
      toast.error(e?.message || 'Kunde inte starta inspelning');
    }
  };

  const pause = () => {
    const rec = mediaRecorderRef.current;
    if (!rec) return;
    if (rec.state === 'recording') {
      rec.pause();
      pauseStartRef.current = Date.now();
      setState('paused');
    } else if (rec.state === 'paused') {
      rec.resume();
      pausedTotalRef.current += (Date.now() - pauseStartRef.current);
      setState('recording');
    }
  };

  const stop = () => {
    try { mediaRecorderRef.current?.stop(); } catch { /* ignore */ }
  };

  const discard = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null);
    setAudioUrl(null);
    setElapsed(0);
    setState('idle');
    setResult(null);
    setLevel(0);
  };

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 24 * 1024 * 1024) {
      toast.error('För stor fil — max ~24 MB 🌸');
      return;
    }
    setAudioBlob(f);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(URL.createObjectURL(f));
    setElapsed(0);
    setState('stopped');
    setResult(null);
    if (e.target) e.target.value = '';
  };

  const transcribe = async () => {
    if (!audioBlob) return;
    setBusy(true);
    try {
      const fd = new FormData();
      const filename = audioBlob.type.includes('webm') ? 'recording.webm'
        : audioBlob.type.includes('mp4') ? 'recording.m4a'
        : audioBlob.type.includes('mpeg') ? 'recording.mp3'
        : audioBlob.type.includes('wav') ? 'recording.wav'
        : (audioBlob.name || 'recording.webm');
      fd.append('audio', audioBlob, filename);
      if (lang) fd.append('language', lang);
      fd.append('save', '1');
      if (elapsed) fd.append('duration_sec', String(elapsed));
      const { data } = await axios.post(`${API}/voice-notes/transcribe`, fd, {
        withCredentials: true,
        timeout: 180000,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data.text) {
        toast('Hittade ingen hörbar text 🌸 Prova igen närmare mikrofonen');
      } else {
        setResult(data);
        toast.success('✨ Sammanfattat och sparat som anteckning');
        loadHistory();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || 'Kunde inte transkribera');
    } finally {
      setBusy(false);
    }
  };

  // Derived UI
  const barHeights = Array.from({ length: 40 }, (_, i) => {
    // pseudo-random waveform driven by current `level`
    const phase = (i * 0.37 + elapsed * 1.7) % (Math.PI * 2);
    return Math.max(8, (Math.sin(phase) * 0.5 + 0.5) * level * 100 + 8);
  });

  return (
    <div className="space-y-5" data-testid="voice-recorder-tab">
      <div className="neo-card p-5 bg-gradient-to-br from-[#F5EEFF] to-[#FFE8F5]">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#C8B6FF] border-2 border-black flex items-center justify-center">
            <Mic className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-['Fredoka']">Spela in 🎙️</h2>
            <p className="text-sm text-gray-700">
              Spela in vad som sägs — vi skriver ner det och sammanfattar varma poäng åt dig 💛
            </p>
          </div>
        </div>
      </div>

      {/* Recorder card */}
      <div className="neo-card p-5 bg-white">
        {/* Waveform / state display */}
        <div className="h-20 rounded-xl bg-[#FFFEF5] border-2 border-black overflow-hidden flex items-center justify-center gap-0.5 px-2 mb-3" data-testid="recorder-waveform">
          {state === 'idle' && !audioBlob ? (
            <p className="text-sm opacity-60">Tryck på 🎙️ för att börja spela in</p>
          ) : (state === 'stopped' && audioUrl) ? (
            <audio src={audioUrl} controls className="w-full" data-testid="recorder-playback" />
          ) : (
            barHeights.map((h, i) => (
              <span
                key={i}
                className="inline-block w-1 rounded-full transition-all duration-100"
                style={{
                  height: `${h}%`,
                  background: state === 'paused' ? '#C8B6FF80' : 'linear-gradient(to top, #FFC8DD, #C8B6FF)',
                }}
              />
            ))
          )}
        </div>

        {/* Timer */}
        <div className="flex items-center justify-center gap-2 mb-3">
          <Clock className="w-4 h-4 opacity-60" />
          <span className="font-mono text-lg font-bold tabular-nums" data-testid="recorder-elapsed">{fmtTime(elapsed)}</span>
          {state === 'recording' && <span className="text-xs text-red-500 animate-pulse">● SPELAR IN</span>}
          {state === 'paused' && <span className="text-xs opacity-60">⏸ Pausad</span>}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap gap-2 justify-center">
          {state === 'idle' && !audioBlob && (
            <>
              <button
                onClick={start}
                className="neo-btn px-5 py-3 bg-[#FFC8DD] font-bold flex items-center gap-2"
                data-testid="recorder-start-btn"
              >
                <Mic className="w-5 h-5" /> Börja spela in
              </button>
              <label className="neo-btn px-5 py-3 bg-white font-bold flex items-center gap-2 cursor-pointer" data-testid="recorder-upload-label">
                <Upload className="w-5 h-5" /> Ladda upp ljudfil
                <input type="file" accept="audio/*" onChange={uploadFile} className="hidden" data-testid="recorder-upload-input" />
              </label>
            </>
          )}
          {(state === 'recording' || state === 'paused') && (
            <>
              <button onClick={pause} className="neo-btn px-4 py-3 bg-[#FFE8A3] flex items-center gap-2" data-testid="recorder-pause-btn">
                {state === 'paused' ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
                {state === 'paused' ? 'Fortsätt' : 'Pausa'}
              </button>
              <button onClick={stop} className="neo-btn px-5 py-3 bg-[#FFC8DD] font-bold flex items-center gap-2" data-testid="recorder-stop-btn">
                <Square className="w-5 h-5" /> Stoppa
              </button>
            </>
          )}
          {state === 'stopped' && audioBlob && (
            <>
              <button
                onClick={transcribe}
                disabled={busy}
                className="neo-btn px-5 py-3 bg-[#B9FBC0] font-bold flex items-center gap-2"
                data-testid="recorder-transcribe-btn"
              >
                {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Transkriberar…</> : <><Sparkles className="w-5 h-5" /> Transkribera & sammanfatta</>}
              </button>
              <button onClick={discard} disabled={busy} className="neo-btn px-3 py-3 bg-white flex items-center gap-1 text-sm" data-testid="recorder-discard-btn">
                <Trash2 className="w-4 h-4" /> Kasta
              </button>
            </>
          )}
        </div>

        {/* Language picker */}
        <div className="flex items-center gap-2 justify-center mt-3 text-xs flex-wrap">
          <span className="font-bold opacity-60">Språk:</span>
          {LANGUAGES.map((l) => (
            <button
              key={l.code || 'auto'}
              onClick={() => setLang(l.code)}
              className={`neo-btn px-2 py-0.5 ${lang === l.code ? 'bg-[#C8B6FF]' : 'bg-white'}`}
              data-testid={`recorder-lang-${l.code || 'auto'}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-3" data-testid="recorder-result">
          <div className="neo-card p-4 bg-gradient-to-br from-[#F5EEFF] to-white">
            <p className="text-[11px] font-bold uppercase opacity-60 mb-1 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Sammanfattning
            </p>
            <pre className="whitespace-pre-wrap text-sm font-['Nunito'] leading-relaxed" data-testid="recorder-summary">{result.summary || '(ingen sammanfattning tillgänglig)'}</pre>
            {result.note_id && (
              <p className="text-[11px] text-gray-500 mt-2 flex items-center gap-1">
                <Save className="w-3 h-3" /> Sparad som anteckning
              </p>
            )}
          </div>
          <details className="neo-card p-3 bg-[#FFFEF5]">
            <summary className="cursor-pointer text-xs font-bold opacity-70">📝 Full transkription ({result.char_count || (result.text || '').length} tecken)</summary>
            <p className="whitespace-pre-wrap text-sm mt-2 leading-relaxed" data-testid="recorder-transcript">{result.text}</p>
          </details>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="neo-card p-4">
          <p className="text-sm font-bold mb-2 flex items-center gap-1"><Volume2 className="w-4 h-4" /> Tidigare inspelningar</p>
          <div className="space-y-2">
            {history.map((n) => (
              <div key={n.id} className="neo-card p-2.5 bg-white" data-testid={`voice-history-${n.id}`}>
                <button
                  onClick={() => setExpandedId(expandedId === n.id ? null : n.id)}
                  className="w-full flex items-center gap-2 text-left"
                >
                  <span className="w-2 h-2 rounded-full" style={{ background: n.color || '#C8B6FF' }} />
                  <p className="text-xs font-bold truncate flex-1">{n.title}</p>
                  <span className="text-[10px] opacity-60">
                    {n.duration_sec ? fmtTime(n.duration_sec) : ''}
                  </span>
                </button>
                {expandedId === n.id && (
                  <div className="mt-2 pt-2 border-t border-dashed border-gray-200">
                    {n.summary && (
                      <div className="mb-2">
                        <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Sammanfattning</p>
                        <pre className="whitespace-pre-wrap text-xs bg-[#F5EEFF] rounded p-2 leading-relaxed">{n.summary}</pre>
                      </div>
                    )}
                    <p className="text-[10px] font-bold uppercase opacity-60 mb-1">Full text</p>
                    <p className="whitespace-pre-wrap text-xs bg-[#FFFEF5] rounded p-2 leading-relaxed max-h-60 overflow-auto">{n.content}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
